import { endOfDay, startOfDay } from 'date-fns'
import { FC, ReactNode, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { TableContainer, Card, Link, Chip, Box } from '@mui/material'

import { GRTable, GRTableColumn, SortOrder } from '../../../../components/GRTable/GRTable'
import { formatEventInstanceDuration } from '../../../../helpers/date'
import { useDateTimeFormatter } from '../../hooks/useDateTimeFormatter'
import { EventDialogTab } from '../../hooks/useEventDialogTabs'
import { useLiveEventModal } from '../../hooks/useLiveEventModal'
import { useLiveEventTagGroupsMapByEventTypeId } from '../../hooks/useLiveEventTagGroups'
import { useLiveEventTypeModal } from '../../hooks/useLiveEventTypeModal'
import { EventInstance } from '../../types/EventInstance'
import { LiveEventTagGroup } from '../../types/LiveEventTagGroup'
import { EventHighlightButton } from '../EventHighlightButton/EventHighlightButton'
import { EventTagGroupIndicator } from '../EventTagGroupIndicator/EventTagGroupIndicator'

type LiveEventInstanceTableProps = {
  instances: EventInstance[]
  isLoading?: boolean
  children?: ReactNode
  onEventHighlighted?: (eventId: string) => void
}

const dateFormat: Intl.DateTimeFormatOptions = {
  weekday: 'short',
  year: 'numeric',
  month: 'short',
  day: 'numeric',
}

export const LiveEventInstanceTable: FC<LiveEventInstanceTableProps> = ({ instances, isLoading, onEventHighlighted, children }) => {
  const { t } = useTranslation()
  const dateFormatter = useDateTimeFormatter(dateFormat)
  const { showModal: showEventModal } = useLiveEventModal()
  const { showModal: showEventTypeModal } = useLiveEventTypeModal()
  const liveEventTagGroupsMapByEventTypeId = useLiveEventTagGroupsMapByEventTypeId()

  const customTableProps = useMemo(
    () => ({
      showEventTypeModal,
      showEventModal,
      onEventHighlighted,
    }),
    [onEventHighlighted, showEventTypeModal, showEventModal]
  )

  const eventInstanceColumns: GRTableColumn<EventInstance, typeof customTableProps>[] = useMemo(
    () => [
      {
        labelAccessor: '',
        columns: [
          {
            labelAccessor: t('live-events:event_instance_table_column_event_type'),
            accessor: ({ row, customTableProps }) => {
              const tagGroup = liveEventTagGroupsMapByEventTypeId[row.eventTypeId]
              const isSpecialEventType = tagGroup?.id === LiveEventTagGroup.DEFINING_OCCURRENCES

              return (
                <>
                  <EventTagGroupIndicator colorHex={row.colorHex} />
                  {isSpecialEventType ? (
                    <span style={{ color: 'inherit' }}>{row.eventTypeName}</span>
                  ) : (
                    <Link
                      underline="none"
                      sx={{ color: (theme) => theme.palette.text.primary, '&:hover': { color: (theme) => theme.palette.primary.main, cursor: 'pointer' } }}
                      onClick={() =>
                        customTableProps?.showEventTypeModal({
                          trackedGameId: row.gameId,
                          eventTypeId: row.eventTypeId,
                          tab: EventDialogTab.AllEvents,
                        })
                      }
                    >
                      {row.eventTypeName}
                    </Link>
                  )}
                </>
              )
            },
            cellProps: { align: 'left' },
            sortable: true,
            sortAccessor: ({ row }) => row.eventTypeName,
          },
        ],
      },
      {
        labelAccessor: '',
        columns: [
          {
            labelAccessor: t('live-events:event_instance_table_column_event_name'),
            accessor: ({ row, customTableProps }) => (
              <Box display={'flex'} alignItems={'center'} justifyContent={'space-between'} gap={1}>
                <Link
                  underline="none"
                  sx={{ color: (theme) => theme.palette.text.primary, '&:hover': { color: (theme) => theme.palette.primary.main, cursor: 'pointer' } }}
                  onClick={() =>
                    customTableProps?.showEventModal({
                      trackedGameId: row.gameId,
                      eventTypeId: row.eventTypeId,
                      eventId: row.liveEventId,
                      durationStart: row.start,
                      durationEnd: row.end,
                      tab: EventDialogTab.Description,
                    })
                  }
                >
                  {row.name}
                </Link>
                {!row.active && <Chip label={t('live-events:event_inactive')} color="warning" size="small" sx={{ flexShrink: 0, mb: 0 }} />}
              </Box>
            ),
            cellProps: { align: 'left' },
            sortable: true,
            sortAccessor: ({ row }) => row.name,
          },
        ],
      },
      {
        labelAccessor: '',
        columns: [
          {
            labelAccessor: t('live-events:duration_start'),
            accessor: ({ row }) => dateFormatter.format(row.start),
            cellProps: { align: 'left' },
            sortable: true,
            sortOrder: SortOrder.DESC,
            sortAccessor: ({ row }) => row.start,
          },
        ],
      },
      {
        labelAccessor: '',
        columns: [
          {
            labelAccessor: t('live-events:duration_end'),
            accessor: ({ row }) => dateFormatter.format(row.end),
            cellProps: { align: 'left' },
            sortable: true,
            sortAccessor: ({ row }) => row.end,
          },
        ],
      },
      {
        labelAccessor: '',
        columns: [
          {
            labelAccessor: t('live-events:duration'),
            accessor: ({ row }) => {
              return formatEventInstanceDuration(row.start, row.end)
            },
            cellProps: { align: 'left' },
            sortable: true,
            sortAccessor: ({ row }) => endOfDay(row.end).getTime() - startOfDay(row.start).getTime(),
          },
        ],
      },
      {
        labelAccessor: '',
        columns: [
          {
            labelAccessor: t('live-events:event_instance_table_column_instance_count'),
            accessor: ({ row, customTableProps }) => row.instanceCount,
            cellProps: { align: 'center' },
            headerCellProps: { width: 100 },
            sortable: true,
            sortAccessor: ({ row }) => row.instanceCount,
          },
        ],
      },
      {
        labelAccessor: '',
        columns: [
          {
            labelAccessor: '',
            accessor: ({ row, customTableProps }) => (
              <EventHighlightButton
                variant="fab"
                eventId={row.liveEventId}
                eventType={row.eventTypeId}
                eventStartTime={row.start}
                eventEndTime={row.end}
                trackedGameId={row.gameId}
                onEventHighlighted={() => customTableProps?.onEventHighlighted?.(row.liveEventId)}
              />
            ),
            cellProps: { align: 'center' },
            sortable: false,
            sortAccessor: ({ row }) => endOfDay(row.end).getTime() - startOfDay(row.start).getTime(),
          },
        ],
      },
    ],
    [dateFormatter, t, liveEventTagGroupsMapByEventTypeId]
  )

  const [columns, setColumns] = useState<GRTableColumn<EventInstance, typeof customTableProps>[]>(eventInstanceColumns)
  const handleColumnsUpdate = useCallback((updatedColumns: GRTableColumn<EventInstance, typeof customTableProps>[]) => {
    setColumns(updatedColumns)
  }, [])

  return (
    <TableContainer component={Card}>
      <GRTable
        rows={instances || []}
        columns={columns}
        rowIdKey="id"
        onColumnsUpdated={handleColumnsUpdate}
        isLoading={isLoading}
        customProps={customTableProps}
        noRowsLabel={t('compare:no_results_found')}
      />
    </TableContainer>
  )
}
